import { ChartContainer, ChartConfig, recharts, Card, Badge, lucideIcons } from '@round/creatorbase-ui';
import { formatNumberToKNotation } from 'helpers';
import styles from './Performance.module.css';
import cn from 'classnames';
import toKebabCase from 'lodash/kebabCase';

const { AreaChart, XAxis, YAxis, Area } = recharts;
const { TrendingUp, TrendingDown } = lucideIcons;

const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const getGradientId = (label: string) => {
    return `${toKebabCase(label)}-gradient`;
};

export type Item = {
    timestamp: string;
    value: number;
};

type Props = {
    data: Item[];
    label: string;
    color?: string;
};

const PerformanceChart = ({ data, color, label }: Props) => {
    const config: ChartConfig = {
        value: {
            label,
            color,
        },
        timestamp: {
            label: 'Timestamp',
            color,
        },
    };

    const latestItem = data[data.length - 1] ?? null;
    const firstItem = data[0] ?? null;
    const changePercentage = firstItem ? ((latestItem?.value - firstItem?.value) / firstItem?.value) * 100 : null;
    const isPositiveChange = (changePercentage ?? 0) > 0;
    const sign = isPositiveChange ? '+' : '';

    return (
        <Card>
            <Card.Header>
                <Card.Title className={styles.chartLabel}>{label}</Card.Title>
                <div className={styles.latestValueContainer}>
                    {latestItem && (
                        <span className={styles.latestValue}>{formatNumberToKNotation(latestItem.value)}</span>
                    )}
                    {changePercentage && (
                        <Badge
                            variant={isPositiveChange ? 'secondary' : 'destructive'}
                            className={cn(styles.changeBadge, { [styles.positiveChangeBadge]: isPositiveChange })}
                        >
                            {isPositiveChange ? (
                                <TrendingUp className={styles.trendingIcon} />
                            ) : (
                                <TrendingDown className={styles.trendingIcon} />
                            )}
                            {sign}
                            {changePercentage.toFixed(2)}%
                        </Badge>
                    )}
                </div>
            </Card.Header>

            <Card.Content>
                <ChartContainer config={config} className={styles.chart}>
                    <AreaChart accessibilityLayer data={data}>
                        <defs>
                            <linearGradient id={getGradientId(label)} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                        </defs>

                        <XAxis
                            dataKey="timestamp"
                            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                            tickFormatter={(value: string) => formatDate(value)}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                            dy={4}
                        />

                        <YAxis
                            dataKey="value"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                            tickFormatter={formatNumberToKNotation}
                        />

                        <ChartContainer.Tooltip
                            content={
                                <ChartContainer.TooltipContent
                                    labelClassName={styles.tooltipLabel}
                                    labelFormatter={(label) => formatDate(label as string)}
                                    formatter={(value, name) => {
                                        const itemConfig = config[name as keyof typeof config];
                                        const label = itemConfig?.label;
                                        const formattedValue = formatNumberToKNotation(Number(value));
                                        return (
                                            <span style={{ color: itemConfig?.color, fontSize: '10px' }}>
                                                {label}: {formattedValue}
                                            </span>
                                        );
                                    }}
                                />
                            }
                        />

                        <Area
                            dataKey="value"
                            type="monotone"
                            fill={`url(#${getGradientId(label)})`}
                            stroke={color}
                            strokeWidth={1.5}
                            dot={false}
                            activeDot={{ r: 3, fill: color }}
                        />
                    </AreaChart>
                </ChartContainer>
            </Card.Content>
        </Card>
    );
};

export default PerformanceChart;
